import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  Download, 
  Calendar, 
  User, 
  Truck, 
  Package, 
  MapPin, 
  Eye, 
  Filter, 
  Search, 
  AlertCircle, 
  Loader,
  CheckCircle,
  XCircle,
  ZoomOut,
  ZoomIn,
  RotateCw,
  X,
  Move
} from 'lucide-react';
import { getAuthHeaders } from '../../utils/getauthheaders';


// Types matching your structure
interface CMRDocument {
  id: number;
  shipment_id: string;
  document_type: 'cmr' | 'aviz';
  image_path: string;
  uploaded_by?: number;
  uploaded_by_name?: string;
  created_at: string;
  updated_at: string;
  
  // Date din join-uri
  client_nume?: string;
  client_telefon?: string;
  client_email?: string;
  sofer_nume?: string;
  sofer_telefon?: string;
  vehicul_numar?: string;
  vehicul_marca?: string;
  vehicul_model?: string;
  plecare_oras?: string;
  plecare_judet?: string;
  plecare_adresa?: string;
  destinatie_oras?: string;
  destinatie_judet?: string;
  destinatie_adresa?: string;
  shipment_status?: string;
}

interface CMRStats {
  total_documents: number;
  total_cmr: number;
  total_aviz: number;
  shipments_with_docs: number;
  shipments_without_docs: number;
  total_shipments: number;
}

const RapoarteOperationalePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('luna_curenta');
  const [selectedSofer, setSelectedSofer] = useState('toti');
  const [selectedStatus, setSelectedStatus] = useState('toate');
  const [selectedCMR, setSelectedCMR] = useState<CMRDocument | null>(null);
  const [cmrData, setCmrData] = useState<CMRDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<CMRStats>({
    total_documents: 0,
    total_cmr: 0,
    total_aviz: 0,
    shipments_with_docs: 0,
    shipments_without_docs: 0,
    total_shipments: 0
  });

  // Image viewer state (matching your structure)
  const [viewingImage, setViewingImage] = useState<{ url: string; document: CMRDocument } | null>(null);
  const [imageScale, setImageScale] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });

  const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

  // Fetch CMR data from API
  const fetchCMRData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/cmr`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCmrData(data.data || []);
      } else {
        throw new Error(data.error || 'Eroare la încărcarea datelor');
      }
    } catch (err) {
      console.error('Error fetching CMR data:', err);
      setError(err instanceof Error ? err.message : 'Eroare la încărcarea datelor CMR');
      setCmrData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cmr/stats`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setStats(data.data);
      } else {
        throw new Error(data.error || 'Eroare la încărcarea statisticilor');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      // Don't show error for stats, just keep default values
    }
  };

  // Fetch detailed CMR document
  const fetchCMRDetails = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cmr/${id}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSelectedCMR(data.data);
      } else {
        throw new Error(data.error || 'Eroare la încărcarea detaliilor');
      }
    } catch (err) {
      console.error('Error fetching CMR details:', err);
      alert('Eroare la încărcarea detaliilor CMR: ' + (err instanceof Error ? err.message : 'Eroare necunoscută'));
    }
  };

  // Download function with error handling (matching your structure)
  const handleDownload = async (cmrDoc: CMRDocument) => {
    try {
      setDownloadLoading(true);
      setDownloadError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/cmr/${cmrDoc.id}/image`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Nu s-a putut descărca fișierul');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cmrDoc.document_type}_${cmrDoc.shipment_id}_${cmrDoc.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Show success message
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadError('Nu s-a putut descărca fișierul. Încercați din nou.');
      setTimeout(() => setDownloadError(null), 5000);
    } finally {
      setDownloadLoading(false);
    }
  };

  
  const closeImageViewer = () => {
    setViewingImage(null);
    setImageScale(1);
    setImageRotation(0);
    setImagePosition({ x: 0, y: 0 });
    setImageLoading(false);
    setImageError(null);
    document.removeEventListener('keydown', handleKeyPress);
  };

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (!viewingImage) return;
    
    switch (event.key) {
      case 'Escape':
        closeImageViewer();
        break;
      case 'r':
      case 'R':
        rotateImage();
        break;
      case '+':
      case '=':
        if (event.ctrlKey) {
          event.preventDefault();
          zoomIn();
        }
        break;
      case '-':
        if (event.ctrlKey) {
          event.preventDefault();
          zoomOut();
        }
        break;
      case '0':
        if (event.ctrlKey) {
          event.preventDefault();
          resetImageView();
        }
        break;
    }
  }, [viewingImage]);

  

  
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!viewingImage) return;
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setImageScale(prev => Math.max(0.1, Math.min(5, prev + delta)));
  }, [viewingImage]);

  const resetImageView = () => {
    setImageScale(1);
    setImageRotation(0);
    setImagePosition({ x: 0, y: 0 });
  };

  const zoomIn = () => {
    setImageScale(prev => Math.min(prev + 0.25, 5));
  };

  const zoomOut = () => {
    setImageScale(prev => Math.max(prev - 0.25, 0.1));
  };

  const rotateImage = () => {
    setImageRotation(prev => (prev + 90) % 360);
  };

  useEffect(() => {
    fetchCMRData();
    fetchStats();
  }, []);

  useEffect(() => {
    if (viewingImage) {
      const imageContainer = document.querySelector('.image-container');
      if (imageContainer) {
        imageContainer.addEventListener('wheel', handleWheel, { passive: false });
        return () => imageContainer.removeEventListener('wheel', handleWheel);
      }
    }
  }, [viewingImage, handleWheel]);

  useEffect(() => {
    if (viewingImage) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [viewingImage, handleKeyPress]);

  // Filter data based on search and filters
  const filteredCMRs = cmrData.filter(cmr => {
    const matchesSearch = (
      (cmr.shipment_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cmr.client_nume || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cmr.sofer_nume || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cmr.vehicul_numar || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const matchesSofer = selectedSofer === 'toti' || cmr.sofer_nume === selectedSofer;
    const matchesStatus = selectedStatus === 'toate' || cmr.document_type === selectedStatus;
    
    return matchesSearch && matchesSofer && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'cmr': return 'bg-blue-100 text-blue-800';
      case 'aviz': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'cmr': return 'Document CMR';
      case 'aviz': return 'Aviz Transport';
      default: return 'Necunoscut';
    }
  };

  
  const getStatusTextLivrare = (status) => {
  switch (status) {
    case 'completed': 
    case 'livrat': 
      return 'Finalizat';
    case 'in_transit': 
    case 'pe_drum': 
      return 'În tranzit';
    case 'pending': 
    case 'in_asteptare': 
      return 'În așteptare';
    case 'in_incarcare':
      return 'În încărcare';
    case 'cancelled': 
    case 'anulat':
      return 'Anulat';
    default: return 'Necunoscut';
  }
};
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ro-RO', {
      timeZone: 'Europe/Bucharest'
    });
  };

  const uniqueDrivers = [...new Set(cmrData.map(cmr => cmr.sofer_nume).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-3">
            <Loader className="h-8 w-8 animate-spin text-blue-600" />
            <span className="text-gray-600 font-medium">Se încarcă datele...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-6 bg-gray-50">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <h3 className="text-red-800 font-medium">Eroare la încărcarea datelor</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
              <button 
                onClick={() => fetchCMRData()}
                className="mt-3 text-red-600 hover:text-red-800 text-sm underline font-medium"
              >
                Încearcă din nou
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Rapoarte Operaționale</h1>
        <p className="text-gray-600">Monitorizare completă a documentelor CMR și avizelor de transport</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="bg-blue-100 rounded-lg p-3">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Documente</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_documents}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="bg-green-100 rounded-lg p-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">CMR Documents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_cmr}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="bg-purple-100 rounded-lg p-3">
              <Package className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avize Transport</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_aviz}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="bg-orange-100 rounded-lg p-3">
              <Truck className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Transporturi cu Doc.</p>
              <p className="text-2xl font-bold text-gray-900">{stats.shipments_with_docs}</p>
              <p className="text-xs text-gray-500">din {stats.total_shipments} total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Căutare transport, client, șofer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <select 
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="luna_curenta">Luna curentă</option>
            <option value="saptamana_curenta">Săptămâna curentă</option>
            <option value="azi">Astăzi</option>
            <option value="ultimele_30_zile">Ultimele 30 zile</option>
          </select>
          
          <select 
            value={selectedSofer}
            onChange={(e) => setSelectedSofer(e.target.value)}
            className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="toti">Toți șoferii</option>
            {uniqueDrivers.map(driver => (
              <option key={driver} value={driver}>{driver || 'Fără șofer'}</option>
            ))}
          </select>
          
          <select 
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="toate">Toate tipurile</option>
            <option value="cmr">Document CMR</option>
            <option value="aviz">Aviz Transport</option>
          </select>
        </div>
      </div>

      {/* CMR List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">
            Lista Documente CMR ({filteredCMRs.length})
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transport & Tip Document
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client & Destinație
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Șofer & Vehicul
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Încărcare
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acțiuni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCMRs.map((cmr) => (
                <tr key={cmr.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                        cmr.document_type === 'cmr' ? 'bg-blue-100' : 'bg-green-100'
                      }`}>
                        <FileText className={`w-5 h-5 ${
                          cmr.document_type === 'cmr' ? 'text-blue-600' : 'text-green-600'
                        }`} />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">#{cmr.shipment_id}</div>
                        <div className="text-sm text-gray-500">ID Doc: {cmr.id}</div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(cmr.document_type)}`}>
                          {getStatusText(cmr.document_type)}
                        </span>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{cmr.client_nume || 'N/A'}</div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                      {cmr.plecare_oras|| 'Client'} → {cmr.destinatie_oras || 'N/A'}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{cmr.sofer_nume || 'Fără șofer'}</div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Truck className="h-4 w-4 mr-1" />
                          {cmr.vehicul_numar || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {formatDateTime(cmr.created_at)}
                    </div>
                    <div className="text-xs text-gray-500">
                      <div className="text-xs text-gray-500">
  Încărcat de: {cmr.uploaded_by_name || 'N/A'} 
</div>

                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <div className="text-sm text-gray-900">Document disponibil</div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => fetchCMRDetails(cmr.id)}
                        className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-all"
                        title="Vizualizează detalii"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDownload(cmr)}
                        disabled={downloadLoading}
                        className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-lg transition-all disabled:opacity-50"
                        title="Descarcă document"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredCMRs.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nu există documente CMR</h3>
            <p className="text-sm text-gray-500">
              Nu s-au găsit documente CMR pentru criteriile selectate.
            </p>
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {downloadSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center z-50">
          <CheckCircle className="w-5 h-5 mr-2" />
          Descărcat cu succes!
        </div>
      )}

      {downloadError && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center z-50">
          <XCircle className="w-5 h-5 mr-2" />
          {downloadError}
        </div>
      )}

      {/* CMR Details Modal */}
      {selectedCMR && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-2xl">
              <h3 className="text-xl font-semibold text-gray-900">Detalii Document - #{selectedCMR.shipment_id}</h3>
              <button 
                onClick={() => setSelectedCMR(null)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Document Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Informații Document</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">ID Document:</span> {selectedCMR.id}</div>
                    <div><span className="font-medium">Transport ID:</span> {selectedCMR.shipment_id}</div>
                    <div><span className="font-medium">Tip Document:</span> 
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedCMR.document_type)}`}>
                        {getStatusText(selectedCMR.document_type)}
                      </span>
                    </div>
                    <div><span className="font-medium">Data încărcare:</span> {formatDateTime(selectedCMR.created_at)}</div>
                    <div><span className="font-medium">Încărcat de:</span> {selectedCMR.uploaded_by ? `ID ${selectedCMR.uploaded_by}` : 'N/A'}</div>
                  </div>
                </div>

                {/* Client & Transport Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Informații Client & Transport</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Client:</span> {selectedCMR.client_nume || 'N/A'}</div>
                    <div><span className="font-medium">Telefon Client:</span> {selectedCMR.client_telefon || 'N/A'}</div>
                    <div><span className="font-medium">Email Client:</span> {selectedCMR.client_email || 'N/A'}</div>
                    <div><span className="font-medium">Status Transport:</span> {getStatusTextLivrare(selectedCMR.shipment_status || 'N/A')}</div>
                  </div>
                </div>

                {/* Driver & Vehicle Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Șofer & Vehicul</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Șofer:</span> {selectedCMR.sofer_nume || 'N/A'}</div>
                    <div><span className="font-medium">Telefon Șofer:</span> {selectedCMR.sofer_telefon || 'N/A'}</div>
                    <div><span className="font-medium">Vehicul:</span> {selectedCMR.vehicul_numar || 'N/A'}</div>
                    <div><span className="font-medium">Marca:</span> {selectedCMR.vehicul_marca || 'N/A'}</div>
                    <div><span className="font-medium">Model:</span> {selectedCMR.vehicul_model || 'N/A'}</div>
                  </div>
                </div>

                {/* Addresses */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Adrese Transport</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Plecare:</span> 
                      <div className="ml-4 text-gray-600">
                        {selectedCMR.plecare_adresa || 'N/A'}<br />
                        {selectedCMR.plecare_oras}, {selectedCMR.plecare_judet}
                      </div>
                    </div>
                    <div><span className="font-medium">Destinație:</span>
                      <div className="ml-4 text-gray-600">
                        {selectedCMR.destinatie_adresa || 'N/A'}<br />
                        {selectedCMR.destinatie_oras}, {selectedCMR.destinatie_judet}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Preview */}
              {selectedCMR.image_path && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Previzualizare Document</h4>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <img 
                      src={`${API_BASE_URL}/api/cmr/${selectedCMR.id}/image`}
                      alt={`Document ${selectedCMR.document_type} - ${selectedCMR.shipment_id}`}
                      className="max-w-full h-auto rounded-lg shadow-sm"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-center text-gray-500 py-8" style={{display: 'none'}}>
                      <FileText className="mx-auto h-12 w-12 mb-2" />
                      <p>Nu se poate încărca imaginea documentului</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setSelectedCMR(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Închide
              </button>
              <a
                href={`${API_BASE_URL}/api/cmr/${selectedCMR.id}/image`}
                download
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-flex items-center"
              >
                <Download className="h-4 w-4 inline mr-2" />
                Descarcă Document
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RapoarteOperationalePage;